import React, { useEffect, useRef, useState } from "react";
import {
  FaBuilding,
  FaCalendarAlt,
  FaCheck,
  FaEdit,
  FaExchangeAlt,
  FaHistory,
  FaTimes,
  FaTrash,
  FaUser,
} from "react-icons/fa";
import "../css/pages/BookingHistory.css";
import { usePageTitle } from "../hooks/usePageTitle";
import bookingService from "../services/bookingService";
import { Project, projectService } from "../services/projectService";
import { Unit, unitService } from "../services/unitService";
import {
  formatDate,
} from "../utils/formatters";
import { filterUnitsBySearch, formatUnitDisplay } from "../utils/unitSearch";

const BookingHistory: React.FC = () => {
  usePageTitle();

  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [filteredUnits, setFilteredUnits] = useState<Unit[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [unitSearchTerm, setUnitSearchTerm] = useState("");
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [bookingHistory, setBookingHistory] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  // Load units when project is selected
  useEffect(() => {
    if (selectedProject) {
      loadUnits();
    } else {
      setUnits([]);
      setFilteredUnits([]);
      setSelectedUnit(null);
      setUnitSearchTerm("");
      setBookingHistory(null);
    }
  }, [selectedProject]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowUnitDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filter units based on search term - STANDARDIZED SEARCH
  useEffect(() => {
    if (unitSearchTerm) {
      // Use standardized unit search for exact "B-101" or "101" matching
      let filtered = filterUnitsBySearch(units, unitSearchTerm);

      // Also include additional filtering for unit type, size, and project
      if (filtered.length === 0 || unitSearchTerm.length < 3) {
        filtered = units.filter(
          (unit) =>
            filterUnitsBySearch([unit], unitSearchTerm).length > 0 ||
            unit.unitType
              .toLowerCase()
              .includes(unitSearchTerm.toLowerCase()) ||
            unit.size.toLowerCase().includes(unitSearchTerm.toLowerCase()) ||
            (unit.project?.name || "")
              .toLowerCase()
              .includes(unitSearchTerm.toLowerCase())
        );
      }

      setFilteredUnits(filtered);
    } else {
      setFilteredUnits(units);
    }
  }, [unitSearchTerm, units]);

  const loadProjects = async () => {
    try {
      const activeProjects = await projectService.getProjects(true);
      setProjects(activeProjects);
    } catch (err) {
      setError("Failed to load projects");
    }
  };

  const loadUnits = async () => {
    if (!selectedProject) return;

    try {
      setLoading(true);
      const response = await unitService.getUnits(selectedProject.id, { page: 1, limit: 1000 });
      // Deduplicate by unit ID
      const uniqueUnits = Array.from(
        new Map(response.units.map((u) => [u.id, u])).values()
      );
      setUnits(uniqueUnits);
      setFilteredUnits(uniqueUnits);
    } catch (err) {
      setError("Failed to load units");
    } finally {
      setLoading(false);
    }
  };

  const handleUnitSelect = async (unit: Unit) => {
    setSelectedUnit(unit);
    setUnitSearchTerm(formatUnitDisplay(unit.wing, unit.unitNo));
    setShowUnitDropdown(false);

    try {
      setLoading(true);
      const history = await bookingService.getBookingHistoryByUnit(unit.id);
      setBookingHistory(history);
    } catch (err) {
      setError("Failed to load booking history for this unit");
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return `${formatDate(dateString)} ${date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };

  const getActionIcon = (action: string) => {
    switch (action.toLowerCase()) {
      case "create":
      case "book":
        return <FaEdit className="timeline-icon create" />;
      case "update":
        return <FaEdit className="timeline-icon update" />;
      case "approve":
        return <FaCheck className="timeline-icon approve" />;
      case "reject":
        return <FaTimes className="timeline-icon reject" />;
      case "cancel":
        return <FaTimes className="timeline-icon cancel" />;
      case "transfer":
        return <FaExchangeAlt className="timeline-icon transfer" />;
      case "delete":
        return <FaTrash className="timeline-icon delete" />;
      default:
        return <FaHistory className="timeline-icon default" />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action.toLowerCase()) {
      case "create":
      case "book":
        return "success";
      case "update":
        return "info";
      case "approve":
        return "success";
      case "reject":
        return "danger";
      case "cancel":
        return "warning";
      case "transfer":
        return "info";
      case "delete":
        return "danger";
      default:
        return "default";
    }
  };

  // Combine and sort all timeline events
  const getTimelineEvents = () => {
    if (
      !bookingHistory ||
      !bookingHistory.history ||
      !Array.isArray(bookingHistory.history)
    )
      return [];

    const events: any[] = [];

    // Only add history events (not the booking creation as a separate event)
    // The CREATE history entry will handle the booking creation display
    bookingHistory.history.forEach((record: any) => {
      events.push({
        type: "history",
        action: record.action,
        date: record.actionDate,
        data: record,
        id: `history-${record.id}`,
        bookingData:
          record.action === "CREATE" &&
          bookingHistory.bookings &&
          Array.isArray(bookingHistory.bookings)
            ? bookingHistory.bookings.find(
                (b: any) => b.id === record.bookingId
              )
            : null,
      });
    });

    // Sort by date in reverse order (newest first)
    return events.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  };

  return (
    <div className="page-container">
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError("")} className="error-close">
            ×
          </button>
        </div>
      )}

      <div className="search-section" style={{ background: '#fff', padding: '24px', borderRadius: '8px', marginBottom: '20px' }}>
        <div className="search-container">
          <div className="search-header">
            <FaBuilding className="section-icon" />
            <h3>Select Project and Unit to View History</h3>
          </div>

          {/* Project Selection */}
          <div className="form-group">
            <label htmlFor="project-select">Project:</label>
            <select
              id="project-select"
              value={selectedProject?.id || ""}
              onChange={(e) => {
                const projectId = parseInt(e.target.value);
                const project =
                  projects.find((p) => p.id === projectId) || null;
                setSelectedProject(project);
              }}
              className="form-select"
            >
              <option value="">Select a project...</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name} ({project._count.units} units)
                </option>
              ))}
            </select>
          </div>

          {/* Unit Selection - Only show when project is selected */}
          {selectedProject && (
            <div className="form-group">
              <label htmlFor="unit-search">Unit:</label>
              <div className="searchable-dropdown" ref={dropdownRef}>
                <div className="search-input-container">
                  <FaBuilding className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search units... (e.g., A-101)"
                    value={unitSearchTerm}
                    onChange={(e) => {
                      setUnitSearchTerm(e.target.value);
                      setShowUnitDropdown(true);
                    }}
                    onFocus={() => setShowUnitDropdown(true)}
                    disabled={loading}
                    className="form-input"
                  />
                </div>
                {showUnitDropdown && filteredUnits.length > 0 && (
                  <div className="dropdown-options">
                    {filteredUnits.slice(0, 20).map((unit) => (
                      <div
                        key={unit.id}
                        className="dropdown-option"
                        onClick={() => handleUnitSelect(unit)}
                      >
                        <div className="unit-option">
                          <div className="unit-number">
                            {formatUnitDisplay(unit.wing, unit.unitNo)}
                          </div>
                          <div className="unit-details">
                            {unit.unitType} • {unit.size} •{" "}
                            {unit.project?.name || "No Project"}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {bookingHistory && (
        <div className="history-content">
          <div className="unit-summary-card">
            <div className="unit-header">
              <div className="unit-title">
                <h2>
                  {selectedUnit?.wing
                    ? `${selectedUnit.wing}-${selectedUnit.unitNo}`
                    : selectedUnit?.unitNo}
                </h2>
                <span
                  className={`status-badge ${bookingHistory.unit.currentStatus}`}
                >
                  {bookingHistory.unit.currentStatus
                    .replace("_", " ")
                    .toUpperCase()}
                </span>
              </div>
              <div className="unit-meta">
                <span className="meta-item">
                  <strong>Project:</strong> {bookingHistory.unit.project.name}
                </span>
                <span className="meta-item">
                  <strong>Type:</strong> {bookingHistory.unit.unitType}
                </span>
                <span className="meta-item">
                  <strong>Size:</strong> {bookingHistory.unit.size}
                </span>
              </div>
            </div>
          </div>

          {getTimelineEvents().length > 0 ? (
            <div className="timeline-container">
              <div className="timeline-header">
                <h3>Complete Activity Timeline</h3>
                <p>All booking activities in chronological order</p>
              </div>

              <div className="timeline">
                {getTimelineEvents().map((event, index) => (
                  <div
                    key={event.id}
                    className={`timeline-item ${getActionColor(event.action)}`}
                  >
                    <div className="timeline-marker">
                      {getActionIcon(event.action)}
                    </div>

                    <div className="timeline-content">
                      <div className="timeline-header-item">
                        <h4 className="timeline-title">
                          {event.action === "CREATE"
                            ? "Create Booking"
                            : event.action.charAt(0).toUpperCase() +
                              event.action.slice(1)}
                        </h4>
                        <span className="timeline-date">
                          {formatDateTime(event.date)}
                        </span>
                      </div>

                      <div className="timeline-body">
                        {event.action === "CREATE" && event.bookingData ? (
                          <div className="booking-details">
                            <div className="detail-grid">
                              <div className="detail-item">
                                <FaUser className="detail-icon" />
                                <span>
                                  <strong>Customer:</strong>{" "}
                                  {event.bookingData.applicants &&
                                  Array.isArray(event.bookingData.applicants)
                                    ? `${
                                        event.bookingData.applicants.find(
                                          (a: any) => a.isPrimary
                                        )?.firstName || ""
                                      } ${
                                        event.bookingData.applicants.find(
                                          (a: any) => a.isPrimary
                                        )?.surname || ""
                                      }`.trim() || "N/A"
                                    : "N/A"}
                                </span>
                              </div>
                              <div className="detail-item">
                                <FaCalendarAlt className="detail-icon" />
                                <span>
                                  <strong>Source:</strong>{" "}
                                  {event.bookingData.source}
                                </span>
                              </div>
                            </div>
                            <div className="booking-id">
                              <strong>Booking ID:</strong>{" "}
                              {event.bookingData.bookingId}
                            </div>
                            {event.bookingData.remarks && (
                              <div className="remarks">
                                <strong>Remarks:</strong>{" "}
                                {event.bookingData.remarks}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="action-details">
                            {event.data.actionData &&
                              typeof event.data.actionData === "object" && (
                                <div className="action-data">
                                  {Object.entries(event.data.actionData).map(
                                    ([key, value]) => {
                                      // Special handling for transfer actions
                                      if (event.action === "TRANSFER") {
                                        if (key === "oldUnitId" || key === "newUnitId") {
                                          // Skip these - we'll use the Ref versions instead
                                          return null;
                                        }
                                        if (key === "oldUnitRef") {
                                          return (
                                            <div key={key} className="data-item">
                                              <strong>Old Unit:</strong>
                                              <span style={{
                                                padding: '4px 8px',
                                                background: '#fef3c7',
                                                color: '#92400e',
                                                borderRadius: '4px',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                              }}>
                                                {String(value)}
                                              </span>
                                            </div>
                                          );
                                        }
                                        if (key === "newUnitRef") {
                                          return (
                                            <div key={key} className="data-item">
                                              <strong>New Unit:</strong>
                                              <span style={{
                                                padding: '4px 8px',
                                                background: '#d1fae5',
                                                color: '#065f46',
                                                borderRadius: '4px',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                              }}>
                                                {String(value)}
                                              </span>
                                            </div>
                                          );
                                        }
                                      }
                                      
                                      return (
                                        <div key={key} className="data-item">
                                          <strong>
                                            {key
                                              .replace(/([A-Z])/g, " $1")
                                              .replace(/^./, str => str.toUpperCase())}
                                            :
                                          </strong>
                                          <span>
                                            {typeof value === "object"
                                              ? JSON.stringify(value)
                                              : String(value)}
                                          </span>
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="no-history">
              <div className="no-history-content">
                <FaHistory className="no-history-icon" />
                <h3>No History Available</h3>
                <p>This unit has no booking history or activity records.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {!bookingHistory && !loading && (
        <div className="no-selection">
          <div className="no-selection-content">
            <FaHistory className="no-selection-icon" />
            <h3>Select a Unit</h3>
            <p>
              Choose a unit from the search above to view its complete booking
              history and activity timeline.
            </p>
          </div>
        </div>
      )}

      {loading && (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading history...</p>
        </div>
      )}
    </div>
  );
};

export default BookingHistory;
